// @fileoverview : 경남-품질관리 > 변경점정보관리
import { ActionIcon, Avatar, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  ItemChangesGet200Response,
  ItemChangesGet200ResponseRowsInner,
  MasterApiItemChangesItemChangeIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { mutateItemChanges } from "@/api/itemChange/useItemChangeQuery";
import { DateHeader } from "@/features/ledgersAll/components/DateHeader";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";

export const ChangeListPage = () => {
  const { t } = useTranslation();
  const today = dayjs().startOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(7, "day").startOf("day").toDate();
  const queryClient = useQueryClient();
  const { query, setQuery, startDate, endDate, selectedRows } = useStandardLayout();
  const navigate = useNavigate();
  const { openModal } = useModal();

  const { mutate: deleteMutate } = useMutation({
    mutationFn: (params: MasterApiItemChangesItemChangeIdDeleteRequest) =>
      mutateItemChanges
        .delete(params)
        .mutationFn(params as any | MasterApiItemChangesItemChangeIdDeleteRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itemChanges"] });
      customAlert(
        `${t("message.deleteSuccess", { item: `${t("itemChange.change")}` })}`,
        t("common.deleteSuccess"),
        "green",
      );
    },
  });
  useEffect(() => {
    setQuery((prev) => {
      const newQuery = [
        {
          createdAt: {
            $gte: startDate
              ? dayjs(startDate).startOf("day").toISOString()
              : oneWeekAgo.toISOString(),
            $lte: endDate ? dayjs(endDate).endOf("day").toISOString() : today.toISOString(),
          },
        },
      ];

      return newQuery;
    });
  }, [startDate, endDate]);
  const columns: readonly Column<ItemChangesGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
    },
    {
      key: "item.name",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify={"left"} align="left" h="100%">
            {row.item?.name}
          </Flex>
        );
      },
    },
    {
      key: "title",
      name: t("itemChange.title"),
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: t("user.created"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify="flex-start" align="center" h="100%">
            {dayjs(row.createdAt).format("YYYY-MM-DD")}
          </Flex>
        );
      },
    },
    {
      key: "creatorUserName",
      name: t("invoice.creatorUser"),
      sortable: true,
      resizable: true,
    },
    {
      key: "actions",
      name: t("common.action"),
      sortable: false,
      resizable: false,
      formatter: ({ row }) => {
        const handleClick = () => {
          navigate("/quality/change-register", { state: { pageKey: "info", row } });
        };
        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon variant="subtle" color="yellow" onClick={handleClick}>
              <IconEdit />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red">
              <IconTrash
                onClick={() =>
                  deleteMutate({
                    itemChangeId: row.id as number,
                  })
                }
              />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex direction="column" p="sm" justify="space-between" rowGap="md">
        <DateHeader />
      </Flex>
      <Flex p="sm" justify="space-between" rowGap="md">
        <Button
          onClick={() => navigate("/quality/change-register", { state: { pageKey: "register" } })}
        >
          등록하기
        </Button>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="1.25rem" fw={600}>
            {t("itemChange.change")}
          </Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" className={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline" radius="sm">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex className={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<ItemChangesGet200Response, ItemChangesGet200ResponseRowsInner>
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<ItemChangesGet200Response> />
      </Flex>
    </Flex>
  );
};
